import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getConsentChoice,
  saveConsentChoice,
  OPEN_COOKIE_BANNER_EVENT,
  type ConsentCategories,
} from '../lib/consent';

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 leading-snug">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-[#E85D56]' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Na de eerste paint (en dus na hydration; de prerender bevat de banner niet).
    const timer = setTimeout(() => {
      if (getConsentChoice() === null) setVisible(true);
    }, 0);

    function onOpen() {
      // Bij heropenen via de footer: toon de bestaande keuze.
      const current = getConsentChoice();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setVisible(true);
    }
    window.addEventListener(OPEN_COOKIE_BANNER_EVENT, onOpen);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(OPEN_COOKIE_BANNER_EVENT, onOpen);
    };
  }, []);

  useEffect(() => {
    // Scroll-lock zolang de modal open staat, zodat de keuze niet te omzeilen
    // is door verder te scrollen.
    if (!visible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  if (!visible) return null;

  function save(choice: ConsentCategories) {
    saveConsentChoice(choice);
    setVisible(false);
  }

  return (
    // Maximale z-index: widgets van derden (zoals de LeadInfo-chat via GTM)
    // gebruiken extreem hoge z-indexen en mogen nooit over de cookiekeuze
    // heen vallen.
    <div
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Cookievoorkeuren"
    >
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl border border-gray-200 shadow-2xl p-5 sm:p-6">
        <p className="font-semibold text-gray-900 mb-1">Cookies op puntuit.nl</p>
        <p className="text-sm text-gray-600 leading-relaxed mb-3">
          We gebruiken functionele cookies voor een goed werkende website. Kies zelf of je ook
          analytische en marketingcookies toestaat. Lees meer in onze{' '}
          <Link to="/privacyverklaring" className="underline underline-offset-2">privacyverklaring</Link>.
        </p>

        <div className="divide-y divide-gray-100 border-y border-gray-100 mb-4">
          <div className="flex items-start justify-between gap-3 py-2">
            <div>
              <p className="text-sm font-medium text-gray-900">Functioneel</p>
              <p className="text-xs text-gray-500 leading-snug">Nodig voor een goed werkende website.</p>
            </div>
            <span className="mt-1 shrink-0 text-xs font-medium text-gray-400">Altijd aan</span>
          </div>
          <Toggle
            checked={analytics}
            onChange={setAnalytics}
            label="Analytisch"
            description="Meten hoe de website wordt gebruikt, om hem te verbeteren."
          />
          <Toggle
            checked={marketing}
            onChange={setMarketing}
            label="Marketing"
            description="Meten hoe onze campagnes presteren (o.a. Google Ads)."
          />
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => save({ analytics: true, marketing: true })}
            className="w-full px-4 py-2.5 rounded-xl bg-[#E85D56] hover:bg-[#D14A43] text-sm font-semibold text-white transition-colors"
          >
            Alles accepteren
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => save({ analytics: false, marketing: false })}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Weigeren
            </button>
            <button
              type="button"
              onClick={() => save({ analytics, marketing })}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Voorkeuren opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
